import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, MoreVertical, MessageCircle, Wallet, Link2, Pencil, Trash2, Phone,
  FileText, CheckCircle2, Clock, AlertCircle, Landmark,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import StatusBadge, { AckBadge } from "@/components/StatusBadge";
import RemindSheet from "@/components/RemindSheet";
import RecordPaymentSheet from "@/components/RecordPaymentSheet";
import PayDebtSheet from "@/components/PayDebtSheet";
import AttachRepaymentSheet from "@/components/AttachRepaymentSheet";
import AckSheet from "@/components/AckSheet";
import ConfirmPaymentSheet from "@/components/ConfirmPaymentSheet";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtNaira, fmtDate, dueLabel, symbolFor, isIOwe, settlementMeta, confirmationMeta } from "@/lib/format";

const ACTIVITY_LABEL = {
  debt_created: "recorded",
  payment_recorded: "payment recorded",
  debt_fully_paid: "fully repaid",
  acknowledgement_requested: "Acknowledgement requested",
  acknowledged: "Acknowledged ✓",
  disputed: "Details questioned",
  repayment_confirmation_requested: "Repayment confirmation requested",
  repayment_confirmed: "Repayment confirmed ✓",
  repayment_questioned: "Repayment questioned",
  reminder_generated: "Reminder generated",
  debt_edited: "Details edited",
};

function ActivityIcon({ type }) {
  const cls = "h-4 w-4";
  if (type === "acknowledged" || type === "debt_fully_paid" || type === "repayment_confirmed") return <CheckCircle2 className={`${cls} text-emerald-600`} />;
  if (type === "disputed" || type === "repayment_questioned") return <AlertCircle className={`${cls} text-amber-600`} />;
  if (type === "payment_recorded") return <Wallet className={`${cls} text-primary`} />;
  return <Clock className={`${cls} text-muted-foreground`} />;
}

export default function DebtDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [remind, setRemind] = useState(false);
  const [pay, setPay] = useState(false);
  const [payDebt, setPayDebt] = useState(false);
  const [attachAcct, setAttachAcct] = useState(false);
  const [brBusy, setBrBusy] = useState(null);
  const handleBankRep = async (tok, action) => {
    setBrBusy(tok);
    try {
      await api.post(`/br/${tok}/${action}`);
      toast.success(action === "confirm" ? "Payment confirmed" : "Marked as not received");
      refetchAll();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBrBusy(null);
    }
  };
  const [ack, setAck] = useState(false);
  const [confirmPayment, setConfirmPayment] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [editAmount, setEditAmount] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editDue, setEditDue] = useState("");

  const { data: debt, isLoading, isError } = useQuery({
    queryKey: ["debt", id],
    queryFn: async () => (await api.get(`/debts/${id}`)).data,
    retry: false,
  });

  // Deep link from a "Payment reported" notification: bring the confirm card into view.
  useEffect(() => {
    if (debt && window.location.hash === "#repayments") {
      setTimeout(() => document.getElementById("repayments")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [debt]);

  const refetchAll = () => {
    qc.invalidateQueries({ queryKey: ["debt", id] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["debts"] });
    qc.invalidateQueries({ queryKey: ["people"] });
  };

  const openEdit = () => {
    setEditAmount(String(debt.original_amount));
    setEditDesc(debt.description || "");
    setEditDue(debt.due_date ? debt.due_date.slice(0, 10) : "");
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/debts/${id}`, {
        amount: parseFloat(editAmount),
        description: editDesc || null,
        due_date: editDue ? new Date(editDue).toISOString() : null,
      });
      toast.success("Debt updated");
      setEditOpen(false);
      refetchAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/debts/${id}`);
      toast.success("Record deleted");
      refetchAll();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (isError) {
    return (
      <div data-testid="debt-unavailable" className="debii-shell min-h-screen px-5 py-16 text-center space-y-3">
        <p className="font-display text-xl font-bold">This record isn't available</p>
        <p className="text-sm text-muted-foreground">It may have been deleted, or it belongs to another account.</p>
        <Button data-testid="debt-unavailable-home" onClick={() => navigate("/", { replace: true })} className="rounded-xl">Go to Home</Button>
      </div>
    );
  }

  if (isLoading || !debt) {
    return (
      <div className="debii-shell min-h-screen px-5 py-6 space-y-4">
        <Skeleton className="h-8 w-24 rounded-lg" />
        <Skeleton className="h-40 w-full rounded-3xl" />
        <Skeleton className="h-32 w-full rounded-2xl" />
      </div>
    );
  }

  const isPaid = debt.status === "paid";
  const iOwe = isIOwe(debt);
  const sm = isPaid ? settlementMeta(debt.settlement_status) : null;
  const payments = debt.payments || [];
  const bankReps = debt.bank_repayments || [];
  const brMeta = { awaiting_confirmation: { label: "Awaiting confirmation", tone: "amber" }, confirmed: { label: "Confirmed", tone: "emerald" }, not_confirmed: { label: "Not confirmed", tone: "slate" } };

  return (
    <div className="debii-shell min-h-screen px-5 py-4 pb-8">
      <div className="flex items-center justify-between">
        <button data-testid="detail-back" onClick={() => navigate(-1)} className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center">
          <ArrowLeft className="h-5 w-5 text-primary" />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="detail-menu" className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center">
              <MoreVertical className="h-5 w-5 text-primary" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-xl">
            <DropdownMenuItem data-testid="menu-edit" onClick={openEdit}><Pencil className="h-4 w-4" /> Edit debt</DropdownMenuItem>
            <DropdownMenuItem data-testid="menu-ack" onClick={() => setTimeout(() => setAck(true), 0)}><Link2 className="h-4 w-4" /> Acknowledgement</DropdownMenuItem>
            <DropdownMenuItem data-testid="menu-delete" onClick={() => setDelOpen(true)} className="text-red-600"><Trash2 className="h-4 w-4" /> Delete record</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Hero */}
      <div className="mt-4 text-center animate-rise">
        <p className="text-lg font-semibold text-foreground flex items-center justify-center gap-2">
          {debt.person_name}
          {iOwe && <span className="rounded-full bg-foreground/5 px-2 py-0.5 text-xs font-bold text-foreground/70">You owe</span>}
        </p>
        <p data-testid="detail-outstanding" className="mt-2 font-display text-5xl font-extrabold tabular text-foreground">
          {fmtNaira(debt.outstanding_amount, debt.currency)}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">{dueLabel(debt)}</p>
        <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
          {isPaid ? (
            <span
              data-testid="detail-settlement-badge"
              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${sm.tone === "emerald" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : sm.tone === "amber" ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}
            >
              {sm.label}
            </span>
          ) : (
            <StatusBadge status={debt.display_status} currency={debt.currency} testId="detail-status-badge" />
          )}
          <AckBadge status={debt.acknowledgement_status} />
        </div>
      </div>

      {/* Meta */}
      <div className="mt-6 rounded-2xl border border-border bg-card divide-y divide-border">
        <Row label={iOwe ? "Amount borrowed" : "Original amount"} value={fmtNaira(debt.original_amount, debt.currency)} />
        <Row label={iOwe ? "You've repaid" : "Repaid so far"} value={fmtNaira(debt.total_paid, debt.currency)} />
        <Row label={iOwe ? "Date borrowed" : "Date lent"} value={fmtDate(debt.date_lent)} />
        {debt.due_date && <Row label="Expected repayment" value={fmtDate(debt.due_date)} />}
        {debt.person_phone && <Row label="Phone" value={debt.person_phone} icon={Phone} />}
        {debt.description && <Row label="Reason" value={debt.description} icon={FileText} />}
      </div>

      {/* Actions */}
      {!isPaid && (
        <div className={`mt-4 grid gap-3 ${iOwe ? "grid-cols-1" : "grid-cols-2"}`}>
          {iOwe && debt.can_pay && (
            <Button data-testid="pay-debt-button" onClick={() => setPayDebt(true)} className="h-12 rounded-xl">
              <Landmark className="h-5 w-5" /> Pay debt
            </Button>
          )}
          <Button data-testid="record-repayment-button" variant={iOwe && debt.can_pay ? "outline" : "default"} onClick={() => setPay(true)} className="h-12 rounded-xl">
            <Wallet className="h-5 w-5" /> {iOwe ? "Record repayment" : "Record payment"}
          </Button>
          {!iOwe && (
            <Button data-testid="whatsapp-reminder-button" variant="outline" onClick={() => setRemind(true)} className="h-12 rounded-xl">
              <MessageCircle className="h-5 w-5" /> Remind
            </Button>
          )}
        </div>
      )}
      {!isPaid && iOwe && !debt.can_pay && (
        <p data-testid="no-repayment-details" className="mt-3 text-center text-xs text-muted-foreground">
          {debt.repayment_account ? "" : `${debt.person_name} hasn't added repayment details yet.`}
        </p>
      )}

      {/* Scenario A: creditor sets where the debtor can repay (owed-to-me only) */}
      {!isPaid && !iOwe && debt.currency === "NGN" && (
        debt.repayment_account ? (
          <div className="mt-4 rounded-2xl border border-border bg-card p-4" data-testid="debt-repayment-account">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Repayment account</p>
                <p className="font-display font-bold text-foreground">{debt.repayment_account.account_name}</p>
                <p className="text-sm text-muted-foreground">{debt.repayment_account.bank_name} {debt.repayment_account.account_masked}</p>
              </div>
              <Landmark className="h-5 w-5 text-primary shrink-0" />
            </div>
            <button data-testid="change-repayment-account" onClick={() => setAttachAcct(true)} className="mt-3 text-sm font-semibold text-primary">Change account</button>
          </div>
        ) : (
          <div className="mt-4 rounded-2xl border border-dashed border-primary/40 bg-secondary/60 p-4" data-testid="add-repayment-prompt">
            <p className="font-display font-bold text-foreground">Make repayment easier</p>
            <p className="mt-0.5 text-sm text-muted-foreground">Add the bank account where {debt.person_name} can repay you.</p>
            <div className="mt-3 flex gap-2">
              <Button data-testid="add-repayment-account-btn" onClick={() => setAttachAcct(true)} className="h-10 rounded-xl">
                <Landmark className="h-4 w-4" /> Add repayment account
              </Button>
            </div>
          </div>
        )
      )}
      {isPaid && (
        <div className={`mt-4 rounded-2xl border p-4 text-center ${sm.tone === "amber" ? "bg-amber-50 border-amber-200" : sm.tone === "slate" ? "bg-slate-50 border-slate-200" : "bg-emerald-50 border-emerald-200"}`}>
          <p className={`font-display font-bold ${sm.tone === "amber" ? "text-amber-800" : sm.tone === "slate" ? "text-slate-700" : "text-emerald-800"}`}>{sm.label}</p>
          <p className={`text-sm mt-0.5 ${sm.tone === "amber" ? "text-amber-700" : sm.tone === "slate" ? "text-slate-600" : "text-emerald-700"}`}>
            {iOwe
              ? `You've fully repaid ${fmtNaira(debt.original_amount, debt.currency)} to ${debt.person_name}.`
              : `${debt.person_name} has fully repaid ${fmtNaira(debt.original_amount, debt.currency)}.`}
          </p>
        </div>
      )}

      {/* Repayments */}
      {payments.length > 0 && (
        <section className="mt-8">
          <h2 className="text-sm font-bold text-foreground uppercase tracking-wide mb-3">Repayments</h2>
          <div className="space-y-2.5">
            {payments.map((p) => {
              const cm = confirmationMeta(p.confirmation_status, debt.person_name);
              const canConfirm = p.confirmation_status !== "confirmed";
              return (
                <div key={p.id} data-testid={`repayment-row-${p.id}`} className="rounded-2xl border border-border bg-card p-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-display font-bold text-foreground tabular">{fmtNaira(p.amount, debt.currency)}</p>
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cm.tone === "emerald" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : cm.tone === "amber" ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      {cm.label}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">{fmtDate(p.payment_date || p.created_at)}</span>
                    {canConfirm && (
                      <button
                        data-testid={`repayment-confirm-btn-${p.id}`}
                        onClick={() => setConfirmPayment(p)}
                        className="text-xs font-bold text-accent"
                      >
                        {p.confirmation_status === "pending" ? "Re-share link" : p.confirmation_status === "questioned" ? "Review & resend" : `Ask ${debt.person_name} to confirm`}
                      </button>
                    )}
                  </div>
                  {p.note && <p className="mt-2 text-sm text-muted-foreground">{p.note}</p>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Reported repayments (direct bank transfers) */}
      {bankReps.length > 0 && (
        <section id="repayments" className="mt-8 scroll-mt-4" data-testid="repayment-history">
          <h2 className="text-sm font-bold text-foreground uppercase tracking-wide mb-3">Repayment history</h2>
          <div className="space-y-2.5">
            {bankReps.map((r) => {
              const m = brMeta[r.status] || { label: r.status, tone: "slate" };
              return (
                <div key={r.id} data-testid={`bankrep-row-${r.id}`} className="rounded-2xl border border-border bg-card p-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-display font-bold text-foreground tabular">{fmtNaira(r.amount, r.currency)}</p>
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${m.tone === "emerald" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : m.tone === "amber" ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      {m.label}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{fmtDate(r.created_at)} · {r.account_masked}</p>
                  {r.confirm_token && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <Button data-testid={`bankrep-confirm-${r.id}`} disabled={brBusy === r.confirm_token} onClick={() => handleBankRep(r.confirm_token, "confirm")} className="h-10 rounded-xl">
                        Confirm payment
                      </Button>
                      <Button data-testid={`bankrep-reject-${r.id}`} variant="outline" disabled={brBusy === r.confirm_token} onClick={() => handleBankRep(r.confirm_token, "reject")} className="h-10 rounded-xl">
                        I haven't received it
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Activity */}
      <section className="mt-8">
        <h2 className="text-sm font-bold text-foreground uppercase tracking-wide mb-3">Activity</h2>
        <div className="space-y-3">
          {debt.activity?.map((a) => (
            <div key={a.id} className="flex items-start gap-3">
              <div className="h-8 w-8 shrink-0 rounded-full bg-secondary flex items-center justify-center mt-0.5">
                <ActivityIcon type={a.activity_type} />
              </div>
              <div className="flex-1">
                <p className="text-sm text-foreground">
                  {a.metadata?.amount ? `${fmtNaira(a.metadata.amount, debt.currency)} ${ACTIVITY_LABEL[a.activity_type] || a.activity_type}` : (ACTIVITY_LABEL[a.activity_type] || a.activity_type)}
                </p>
                <p className="text-xs text-muted-foreground">{fmtDate(a.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <RemindSheet open={remind} onOpenChange={setRemind} debt={debt} />
      <RecordPaymentSheet open={pay} onOpenChange={setPay} debt={debt} onDone={refetchAll} />
      <PayDebtSheet open={payDebt} onOpenChange={setPayDebt} debt={debt} onDone={refetchAll} />
      <AttachRepaymentSheet open={attachAcct} onOpenChange={setAttachAcct} debt={debt} onDone={refetchAll} />
      <AckSheet open={ack} onOpenChange={setAck} debt={debt} onRequested={refetchAll} onEditDetails={openEdit} />
      <ConfirmPaymentSheet
        open={!!confirmPayment}
        onOpenChange={(o) => !o && setConfirmPayment(null)}
        debt={debt}
        payment={confirmPayment}
        onRequested={refetchAll}
      />

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-[24rem] rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">Edit debt</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Amount ({symbolFor(debt.currency)})</label>
              <Input data-testid="edit-amount-input" type="number" value={editAmount} onChange={(e) => setEditAmount(e.target.value)} className="mt-1 h-11 rounded-xl" />
            </div>
            <div>
              <label className="text-sm font-medium">Reason</label>
              <Input value={editDesc} onChange={(e) => setEditDesc(e.target.value)} className="mt-1 h-11 rounded-xl" />
            </div>
            <div>
              <label className="text-sm font-medium">Repay by</label>
              <Input type="date" value={editDue} onChange={(e) => setEditDue(e.target.value)} className="mt-1 h-11 rounded-xl" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="edit-save-button" onClick={saveEdit} className="w-full h-11 rounded-xl">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent className="max-w-[24rem] rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>This removes the debt and its history permanently. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-button" onClick={doDelete} className="rounded-xl bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Row({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-sm text-muted-foreground flex items-center gap-1.5">{Icon && <Icon className="h-4 w-4" />}{label}</span>
      <span className="text-sm font-semibold text-foreground text-right">{value}</span>
    </div>
  );
}
